import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { canUpdateCategoryShare } from '../../utils/categoryShares';
import { API_BASE } from '../../config';
import DataSection from './Form4/DataSection';
import CalculationsSection from './Form4/CalculationsSection';
import DryingCharacterSection from './Form4/DryingCharacterSection';
import CauseSignModal from './Form4/CauseSignModal';
import PickerModal from './Form4/PickerModal';
import YearPickerModal from './Form4/YearPickerModal';
import { getEmptyFormData } from './Form4/constants';
import { calculateIZdor, isSumValid, calculateSKS, calculateOtpadObsh, calculateOtpadTek } from './Form4/utils';
import { usePloshadVydel, useZapasCalculations, useTotalsCalculations } from './Form4/calculationHooks';
import { usePicker } from './Form4/pickerHooks';
import { mobileUiOchags } from '../../utils/mobileCalculations';

const Form4 = ({ handbooks, formData, setFormData, selectedDoc, form2Data, independentForm2Data, dataType, form1Data, form6Data, isActive }) => {

  // Состояние для валидации
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // Состояние для значений сосняк и ОСД (для определения очагов)
  const [sosnyakValue, setSosnyakValue] = useState(0);
  const [osdValue, setOsdValue] = useState(0);

  // Состояние для выбранных очагов (максимум 3)
  const [selectedOchags, setSelectedOchags] = useState(new Set());

  // Состояние для данных очагов (главный/не главный, ВО, фаза, тип)
  const [ochagData, setOchagData] = useState({}); // { causeKey: { isMain: boolean, vo: string, phase: string, type: string } }

  // Состояние для модального окна редактирования очага
  const [isOchagModalOpen, setIsOchagModalOpen] = useState(false);
  const [editingOchagKey, setEditingOchagKey] = useState(null);

  // Состояние для слайдера таблицы
  const [tableScrollPosition, setTableScrollPosition] = useState(0);
  const tableScrollRef = useRef(null);

  // Состояние для слайдера таблицы расчетов
  const [calculationsScrollPosition, setCalculationsScrollPosition] = useState(0);
  const calculationsScrollRef = useRef(null);

  // Состояние для модального окна причины/признака
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCause, setSelectedCause] = useState('');
  const [selectedSign, setSelectedSign] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedField, setSelectedField] = useState('');
  const [isLoadingSigns, setIsLoadingSigns] = useState(false);
  const [loadedSigns, setLoadedSigns] = useState({});
  const [previousCause, setPreviousCause] = useState({});
  const [modalData, setModalData] = useState([]);
  const [modalSubmitError, setModalSubmitError] = useState('');
  const loadSignsForCauseRef = useRef(null);

  // Состояние для picker справочника
  const [showPicker, setShowPicker] = useState(false);
  const [pickerField, setPickerField] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const [activeModalItemId, setActiveModalItemId] = useState(null);

  // Состояние для picker года
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [activeYearItemId, setActiveYearItemId] = useState(null);

  // Состояние для скроллбара модального окна
  const [modalScrollPosition, setModalScrollPosition] = useState(0);
  const modalScrollRef = useRef(null);

  // Ref для отслеживания инициализации
  const isInitializedRef = useRef(false);
  const prevForm2LengthRef = useRef(0);

  // Сбрасываем флаг инициализации и загружаем данные очагов из formData при активации формы
  const prevIsActiveRef = useRef(false);
  const ochagDataLoadedRef = useRef(false);
  
  useEffect(() => {
    if (isActive && !prevIsActiveRef.current) {
      // НЕ сбрасываем флаг инициализации, если данные уже есть
      // Это предотвращает повторную синхронизацию при возврате с других форм
      const hasExistingData = formData?.data && Array.isArray(formData.data) && formData.data.length > 0;
      if (!hasExistingData) {
        isInitializedRef.current = false;
      }
      
      // Загружаем данные очагов из formData при активации формы
      if (formData?.selectedOchags !== undefined) {
        const ochagsArray = Array.isArray(formData.selectedOchags) ? formData.selectedOchags : [];
        setSelectedOchags(new Set(ochagsArray));
        ochagDataLoadedRef.current = true;
      }
      
      if (formData?.ochagData !== undefined) {
        const ochagDataObj = formData.ochagData && typeof formData.ochagData === 'object' 
          ? formData.ochagData 
          : {};
        setOchagData(ochagDataObj);
        ochagDataLoadedRef.current = true;
      }
    }
    
    // Сбрасываем флаг загрузки при деактивации формы
    if (!isActive) {
      ochagDataLoadedRef.current = false;
    }
    
    prevIsActiveRef.current = isActive;
  }, [isActive, formData]);

  // Функция для обновления данных формы
  const updateFormData = useCallback((newData) => {
      setFormData(newData);
  }, [setFormData]);

  // Используем хуки для расчетов
  const { calculatePloshadVydel } = usePloshadVydel(form1Data, dataType);
  
  const {
    calculateZapasSyroVydelForAllYarusy,
    getZapasSuhostoyForAllYarusy,
    getZapasZahObshForAllYarusy,
    calculateSumOfThreeZapasForAllYarusy,
    calculateZapasDensityForAllYarusy,
    calculateTotalDensitySum
  } = useZapasCalculations(form1Data, independentForm2Data, calculatePloshadVydel);
  
  const {
    calculateYarusTotals,
    calculateTotalsByNasazhdeniya,
    getDetailedSumFormula,
    getDetailedTotalsFormulas
  } = useTotalsCalculations(
    formData,
    independentForm2Data,
    form1Data,
    calculatePloshadVydel,
    calculateTotalDensitySum
  );


  // Функции для работы с данными таблицы
  const updateDataItem = useCallback((id, field, value) => {
    const currentFormData = formData || getEmptyFormData();
    const newData = currentFormData.data.map(item => {
      if (item.id === id) {
        const categoryFields = ['osldolap', 'sosldolap', 'usdolap', 'svsuhdolap', 'svvtrdolap', 'svburdolap', 'stsuhdolap', 'stvtrdolap', 'stburdolap'];
        if (categoryFields.includes(field) && !canUpdateCategoryShare(item, field, value, categoryFields)) return item;
        let processedValue;
        
        if (value === '' || value === undefined || value === null) {
          processedValue = 0;
        } else {
          // Удаляем ведущие нули для числовых полей
          const numericFields = ['svsuhdolap', 'svvtrdolap', 'svburdolap', 'stsuhdolap', 'stvtrdolap', 'stburdolap', 'osldolap', 'sosldolap', 'usdolap'];
          if (numericFields.includes(field)) {
            // Удаляем ведущие нули, но оставляем "0" если это единственная цифра
            processedValue = value.replace(/^0+/, '') || '0';
          } else {
            processedValue = value;
          }
        }
        
        const updatedItem = { ...item, [field]: processedValue };
        
        // Если обновляется любой столбец кроме I (здор), пересчитываем I (здор)
        if (field !== 'bpodolap') {
          updatedItem.bpodolap = calculateIZdor(updatedItem);
        }
        
        return updatedItem;
      }
      return item;
    });
    
    const newFormData = { ...currentFormData, data: newData };
    
    updateFormData(newFormData);
  }, [formData, updateFormData, calculateIZdor]);

  // Получаем данные для отображения
  const displayData = useMemo(() => {
    if (formData && formData.data && formData.data.length > 0) {
      return formData;
    }
    return getEmptyFormData();
  }, [formData]);

  // Получаем сохраненные данные модального окна из formData
  const savedModalData = useMemo(() => {
    return displayData.savedModalData || {};
  }, [displayData.savedModalData]);

  // Расчет "сосняк" и ОСД - сумма долей пород с причинами типов DISEASES, STEM_PESTS, CONDITIONAL_ROTTENING
  useEffect(() => {
    if (!displayData?.data || !handbooks || !savedModalData) {
      return;
    }

    // Получаем справочник причин (id=10)
    const causesHandbook = handbooks.find(h => h.id === 10);
    if (!causesHandbook || !causesHandbook.references) {
      return;
    }

    let sosnyakSum = 0;
    let osdSum = 0; // Сумма произведений (доля породы * специальная доля)

    // Проходим по всем строкам данных
    displayData.data.forEach((row) => {
      const rowId = row.id;
      const dolap = parseFloat(row.dolap) || 0;
      
      // Получаем сохраненные данные модального окна для этой строки
      const modalDataForRow = savedModalData[rowId];
      
      if (!modalDataForRow || !Array.isArray(modalDataForRow)) {
        return;
      }

      let hasRequiredCauseTypeInRow = false; // Флаг для "сосняк" - есть ли хотя бы одна причина с нужным типом

      // Проверяем каждую запись в модальном окне на наличие причин с нужными типами
      modalDataForRow.forEach((modalItem) => {
        if (!modalItem.prichina) {
          return;
        }

        // Находим причину в справочнике
        const cause = causesHandbook.references.find(
          r => r.id.toString() === String(modalItem.prichina)
        );

        if (!cause) {
          return;
        }

        // Проверяем тип причины
        const causeType = cause.type || cause.disease_type || '';
        const hasRequiredCauseType = causeType === 'DISEASES' || 
                                     causeType === 'STEM_PESTS' || 
                                     causeType === 'CONDITIONAL_ROTTENING';

        if (hasRequiredCauseType) {
          // Для "сосняк": отмечаем, что в строке есть причина с нужным типом
          hasRequiredCauseTypeInRow = true;

          // Для расчета ОСД: доля породы * специальная доля
          const specificDola = parseFloat(modalItem.specificDolaPrizn) || 0;
          const product = dolap * specificDola;
          osdSum += product;
        }
      });

      // Для "сосняк": если в строке есть хотя бы одна причина с нужным типом, добавляем долю породы один раз
      if (hasRequiredCauseTypeInRow) {
        sosnyakSum += dolap;
      }
    });

    // Расчет ОСД: сумма произведений / 10
    const osd = osdSum / 10;

    // Сохраняем значения в state для использования в определении очагов
    setSosnyakValue(sosnyakSum);
    setOsdValue(osd);
  }, [displayData, savedModalData, handbooks]);

  // Получаем список причин с очагами
  const causesWithOchag = useMemo(() =>
    mobileUiOchags(4, displayData, handbooks, independentForm2Data, form1Data, dataType),
    [displayData, handbooks, independentForm2Data, form1Data, dataType]
  );

  // Функция для получения значения доли поврежденных деревьев для конкретной причины по ID (для карточек очагов)
  const getOchagDamageValueForCause = useCallback((causeId) =>
    causesWithOchag.find(cause => String(cause.id) === String(causeId))?.osd.toFixed(1) ?? '',
    [causesWithOchag]
  );

  // Загрузка данных очагов из form6 при инициализации (только если нет локальных данных)
  const prevForm6DataRef = useRef(null);
  useEffect(() => {
    // Загружаем очаги только если form6Data изменился И нет локальных данных из formData
    if (!form6Data || !handbooks) {
      return;
    }

    // Если уже загрузили данные из formData, проверяем, есть ли там данные о фазе и типе
    // Если нет, загружаем из form6Data
    if (ochagDataLoadedRef.current) {
      // Проверяем, есть ли в formData.ochagData данные о фазе и типе для главного очага
      const hasPhaseOrType = formData?.ochagData && Object.values(formData.ochagData).some(
        ochag => ochag.isMain && (ochag.phase || ochag.type)
      );
      if (hasPhaseOrType) {
        return; // Данные уже есть, не перезаписываем
      }
      // Если данных нет, продолжаем загрузку из form6Data
    }

    // Проверяем, изменились ли данные form6
    const form6DataKey = JSON.stringify({
      selectedOchagCodes: form6Data.selectedOchagCodes || [],
      vidvo: form6Data.vidvo || {}
    });
    
    if (prevForm6DataRef.current === form6DataKey) {
      return; // Данные не изменились, пропускаем
    }
    
    prevForm6DataRef.current = form6DataKey;

    const selectedOchagCodes = form6Data.selectedOchagCodes || [];
    const vidvo = form6Data.vidvo || {};

    if (selectedOchagCodes.length === 0 && Object.keys(vidvo).length === 0) {
      return;
    }

    // Получаем справочник причин (ID 10) для поиска по кодам
    const causesHandbook = handbooks.find(h => h.id === 10);
    if (!causesHandbook || !causesHandbook.references) {
      return;
    }

    // Находим причины по их кодам и отмечаем их галочками
    const newSelectedOchags = new Set();
    const newOchagData = {};

    selectedOchagCodes.forEach((ochagCode) => {
      // Ищем причину по коду в справочнике
      const cause = causesHandbook.references.find(
        r => r.code && r.code.toString() === String(ochagCode)
      );

      if (!cause) {
        return;
      }

      // Ищем соответствующую причину в causesWithOchag (если массив не пустой)
      // Если causesWithOchag пустой, создаем causeKey на основе ID и индекса 0
      let matchingCause = null;
      let causeIndex = -1;
      
      if (causesWithOchag.length > 0) {
        matchingCause = causesWithOchag.find(
          c => c.id.toString() === String(cause.id)
        );
        if (matchingCause) {
          causeIndex = causesWithOchag.findIndex(
            c => c.id.toString() === String(cause.id)
          );
        }
      }
      
      // Если не нашли в causesWithOchag, но причина есть в справочнике, 
      // используем ID причины и индекс 0 (будет обновлено при следующем рендере)
      if (!matchingCause) {
        causeIndex = 0;
      }

      const causeKey = `${cause.id}-${causeIndex}`;
      const causeCode = String(ochagCode); // Код причины из form6Data
      
      // Отмечаем причину галочкой
      newSelectedOchags.add(causeKey);

      // Загружаем данные очага из vidvo, если они есть
      if (vidvo[causeCode]) {
        const ochagInfo = vidvo[causeCode];
        
        // Определяем, является ли очаг главным (если есть ochagPhasa или ochagType)
        const isMain = !!(ochagInfo.ochagPhasa || ochagInfo.ochagType);
        
        // Преобразуем фазу и тип очага из формата form6 в формат Form4
        let phase = '';
        if (ochagInfo.ochagPhasa) {
          // Сохраняем фазу в формате из form6 (INITIAL, POPULATION_GROWTH, OUTBREAK, CRISIS, EMERGING, ACTIVE, FADING)
          phase = ochagInfo.ochagPhasa;
        }
        
        let type = '';
        if (ochagInfo.ochagType) {
          // Преобразуем тип из формата сервера в формат Form4
          if (ochagInfo.ochagType === 'CHRONIC') {
            type = 'chronic';
          } else if (ochagInfo.ochagType === 'EPISODIC') {
            type = 'episodic';
          } else if (ochagInfo.ochagType === 'ACUTE') {
            // Поддержка старого формата ACUTE -> episodic
            type = 'episodic';
          } else {
            // Если тип уже в формате Form4 (chronic, episodic), используем как есть
            type = ochagInfo.ochagType;
          }
        }
        
        // Получаем ID ВО из vidvo
        let voId = '';
        if (ochagInfo.vidvo && ochagInfo.vidvo.id) {
          voId = String(ochagInfo.vidvo.id);
        }

        const ochagDataValue = {
          isMain: isMain,
          vo: voId,
          phase: phase,
          type: type
        };

        // Сохраняем данные очага и по ключу с индексом, и по коду причины (как в Form6)
        newOchagData[causeKey] = ochagDataValue;
        newOchagData[causeCode] = ochagDataValue;
      }
    });

    // Убеждаемся, что только один очаг является главным
    // Группируем по ID причины (игнорируем индекс в ключе и дубликаты по коду)
    const mainOchagsByCauseId = new Map();
    Object.keys(newOchagData).forEach(key => {
      if (newOchagData[key].isMain) {
        // Извлекаем ID причины из ключа (формат: "ID-индекс" или просто код)
        let causeId = null;
        if (key.includes('-')) {
          causeId = key.split('-')[0];
        } else {
          // Если ключ - это код причины, находим ID по коду
          const cause = causesHandbook.references.find(
            r => r.code && String(r.code) === key
          );
          if (cause) {
            causeId = String(cause.id);
          }
        }
        
        // Сохраняем только первый ключ для каждого ID причины
        if (causeId && !mainOchagsByCauseId.has(causeId)) {
          mainOchagsByCauseId.set(causeId, { key, data: newOchagData[key] });
        }
      }
    });
    
    // Оставляем главный статус только у первого найденного главного очага
    let firstMainOchagCauseId = null;
    mainOchagsByCauseId.forEach(({ key, data }, causeId) => {
      if (!firstMainOchagCauseId) {
        firstMainOchagCauseId = causeId;
        // Убеждаемся, что данные сохранены правильно для обоих ключей
        const causeCode = causesHandbook.references.find(
          r => String(r.id) === causeId
        )?.code;
        if (causeCode) {
          newOchagData[String(causeCode)] = { ...data };
        }
      } else {
        // Убираем главный статус у остальных
        newOchagData[key].isMain = false;
        newOchagData[key].phase = '';
        newOchagData[key].type = '';
        
        // Также убираем главный статус у всех ключей с таким же ID причины
        const causeCode = causesHandbook.references.find(
          r => String(r.id) === causeId
        )?.code;
        Object.keys(newOchagData).forEach(otherKey => {
          if (otherKey.startsWith(`${causeId}-`) || (causeCode && otherKey === String(causeCode))) {
            newOchagData[otherKey].isMain = false;
            newOchagData[otherKey].phase = '';
            newOchagData[otherKey].type = '';
          }
        });
      }
    });

    // Обновляем состояние только если есть изменения И нет локальных данных
    if ((newSelectedOchags.size > 0 || Object.keys(newOchagData).length > 0) && !ochagDataLoadedRef.current) {
      setSelectedOchags(prev => {
        // Если локальных данных нет, используем данные из form6Data
        if (prev.size === 0) {
          return newSelectedOchags;
        }
        // Иначе объединяем с существующими
        const combined = new Set(prev);
        newSelectedOchags.forEach(key => combined.add(key));
        return combined;
      });
      setOchagData(prev => {
        // Если локальных данных нет, используем данные из form6Data
        if (Object.keys(prev).length === 0) {
          return newOchagData;
        }
        // Иначе объединяем с существующими
        const merged = { ...prev, ...newOchagData };
        // Убеждаемся, что в объединенных данных только один главный очаг
        let hasMain = false;
        Object.keys(merged).forEach(key => {
          if (merged[key].isMain) {
            if (hasMain) {
              merged[key].isMain = false;
              merged[key].phase = '';
              merged[key].type = '';
            } else {
              hasMain = true;
            }
          }
        });
        return merged;
      });
    }
  }, [form6Data, handbooks, causesWithOchag]);

  // Сохранение данных очагов в formData при их изменении
  const prevSelectedOchagsRef = useRef(null);
  const prevOchagDataRef = useRef(null);
  
  useEffect(() => {
    // Сохраняем данные даже при деактивации формы, чтобы не потерять изменения
    // Убеждаемся, что только один очаг является главным
    // ВАЖНО: считаем уникальные очаги по ID причины, а не по ключам
    const normalizedOchagData = { ...ochagData };
    const mainOchagCauseIds = new Set();
    const causesHandbook = handbooks.find(h => h.id === 10);
    
    // Собираем ID причин всех главных очагов
    Object.keys(normalizedOchagData).forEach(key => {
      if (normalizedOchagData[key]?.isMain) {
        if (key.includes('-')) {
          // Это ключ с ID-индексом, извлекаем ID причины
          const causeId = key.split('-')[0];
          mainOchagCauseIds.add(causeId);
        } else {
          // Это код причины, находим ID
          if (causesHandbook && causesHandbook.references) {
            const cause = causesHandbook.references.find(
              r => r.code && String(r.code) === key
            );
            if (cause) {
              mainOchagCauseIds.add(String(cause.id));
            }
          }
        }
      }
    });

    // Если больше одного уникального главного очага, оставляем только первый
    if (mainOchagCauseIds.size > 1) {
      let firstMainFound = false;
      Object.keys(normalizedOchagData).forEach(key => {
        if (normalizedOchagData[key]?.isMain) {
          let causeId = null;
          if (key.includes('-')) {
            causeId = key.split('-')[0];
          } else if (causesHandbook && causesHandbook.references) {
            const cause = causesHandbook.references.find(
              r => r.code && String(r.code) === key
            );
            if (cause) {
              causeId = String(cause.id);
            }
          }
          
          if (causeId) {
            if (!firstMainFound) {
              firstMainFound = true;
            } else {
              // Убираем главный статус у остальных
              normalizedOchagData[key] = { ...normalizedOchagData[key], isMain: false, phase: '', type: '' };
            }
          }
        }
      });
      
      setOchagData(normalizedOchagData);
      return;
    }

    // Проверяем, изменились ли данные
    const selectedOchagsStr = JSON.stringify(Array.from(selectedOchags).sort());
    const ochagDataStr = JSON.stringify(normalizedOchagData);
    
    if (prevSelectedOchagsRef.current === selectedOchagsStr && prevOchagDataRef.current === ochagDataStr) {
      return; // Данные не изменились
    }
    
    prevSelectedOchagsRef.current = selectedOchagsStr;
    prevOchagDataRef.current = ochagDataStr;

    const selectedOchagsArray = Array.from(selectedOchags);

    // Сохраняем данные в formData (даже при деактивации формы)
    // ВАЖНО: сохраняем selectedOchags всегда, даже если пустой массив
    setFormData(prev => {
      const currentFormData = prev || getEmptyFormData();
      
      // Преобразуем данные очагов в формат form6.vidvo для синхронизации с формой №6
      const vidvo = { ...(currentFormData.form6?.vidvo || {}) };
      const causesHandbook = handbooks.find(h => h.id === 10);
      
      // Проходим по всем данным очагов и обновляем vidvo по коду причины
      Object.keys(normalizedOchagData).forEach(key => {
        // Пропускаем ключи с дефисом (ID-индекс), работаем только с кодами причин
        if (key.includes('-')) {
          return;
        }
        
        const ochagInfo = normalizedOchagData[key];
        if (!ochagInfo) {
          return;
        }
        
        // Находим причину по коду
        if (causesHandbook && causesHandbook.references) {
          const cause = causesHandbook.references.find(
            r => r.code && String(r.code) === key
          );
          
          if (cause) {
            // Преобразуем фазу из формата Form4 в формат Form6
            let ochagPhasa = null;
            if (ochagInfo.phase) {
              // Поддерживаем как старые значения (arising, acting), так и значения в формате сервера
              // (INITIAL, EMERGING, ACTIVE и т.д.). «acting» — это ACTIVE: значения ACTING в МП нет.
              if (ochagInfo.phase === 'arising') {
                ochagPhasa = 'EMERGING';
              } else if (ochagInfo.phase === 'acting') {
                ochagPhasa = 'ACTIVE';
              } else if (typeof ochagInfo.phase === 'string' && ochagInfo.phase.length > 0) {
                // Если фаза уже в формате сервера, используем как есть
                ochagPhasa = ochagInfo.phase;
              }
            }
            
            // Преобразуем тип из формата Form4 в формат Form6
            let ochagType = null;
            if (ochagInfo.type) {
              // Поддерживаем как новые значения (episodic, chronic), так и значения в формате сервера (EPISODIC, CHRONIC)
              if (ochagInfo.type === 'episodic') {
                ochagType = 'EPISODIC';
              } else if (ochagInfo.type === 'chronic') {
                ochagType = 'CHRONIC';
              } else if (typeof ochagInfo.type === 'string' && ochagInfo.type.length > 0) {
                // Если тип уже в формате сервера, используем как есть
                ochagType = ochagInfo.type;
              }
            }
            
            // Преобразуем ВО в формат Form6
            let vidvoObj = null;
            if (ochagInfo.vo) {
              const voHandbook = handbooks.find(h => h.id === 40);
              if (voHandbook && voHandbook.references) {
                const vo = voHandbook.references.find(
                  r => r.id.toString() === String(ochagInfo.vo)
                );
                if (vo) {
                  vidvoObj = { id: vo.id };
                }
              }
            }
            
            // Обновляем или создаем запись в vidvo
            vidvo[key] = {
              ...(vidvo[key] || {}),
              vidvo: vidvoObj,
              ochagPhasa: ochagPhasa,
              ochagType: ochagType
            };
          }
        }
      });
      
      // Удаляем данные очагов, которые больше не выбраны
      const selectedCauseCodes = new Set();
      Array.from(selectedOchags).forEach(key => {
        const causeId = key.split('-')[0];
        if (causesHandbook && causesHandbook.references) {
          const cause = causesHandbook.references.find(
            r => r.id.toString() === causeId
          );
          if (cause && cause.code) {
            selectedCauseCodes.add(String(cause.code));
          }
        }
      });
      
      // Удаляем из vidvo очаги, которые больше не выбраны
      Object.keys(vidvo).forEach(code => {
        if (!selectedCauseCodes.has(code)) {
          delete vidvo[code];
        }
      });
      
      const newFormData = {
        ...currentFormData,
        // ВАЖНО: сохраняем selectedOchags на верхнем уровне formData
        // Это нужно, чтобы форма №6 могла их читать, и они не перезаписывались
        selectedOchags: selectedOchagsArray,
        ochagData: normalizedOchagData,
        form6: {
          ...(currentFormData.form6 || {}),
          vidvo: vidvo
        }
      };
      
      return newFormData;
    });
  }, [selectedOchags, ochagData, setFormData, handbooks]);

  // Функция для загрузки признаков по причине
  const loadSignsForCause = useCallback(async (causeName) => {
    if (!causeName) return;
    
    // Проверяем, есть ли уже загруженные признаки для этой причины
    if (loadedSigns[causeName]) {
      return loadedSigns[causeName];
    }
    
    setIsLoadingSigns(true);
    try {
      const response = await fetch(`${API_BASE}/get-signs/?name=${encodeURIComponent(causeName)}`);
      if (response.ok) {
        const data = await response.json();
        const signs = data.signs || [];
        const signsOptions = signs.map((sign, index) => {
          const signName = typeof sign === 'object' ? sign.name : sign;
          const signId = typeof sign === 'object' ? sign.id : sign;
          const signCode = typeof sign === 'object' ? sign.code : null;
          return {
            value: signId.toString(),
            label: signCode ? `${signName} (${signCode})` : signName,
            code: signCode ? String(signCode) : null,
            description: null
          };
        });
        
        // Сохраняем загруженные признаки
        setLoadedSigns(prev => ({
          ...prev,
          [causeName]: signsOptions
        }));
        
        return signsOptions;
      } else {
        return [];
      }
    } catch (error) {
      return [];
    } finally {
      setIsLoadingSigns(false);
    }
  }, [loadedSigns]);
  
  // Сохраняем функцию в ref для использования в других useCallback
  useEffect(() => {
    loadSignsForCauseRef.current = loadSignsForCause;
  }, [loadSignsForCause]);

  // Функции для работы с модальным окном
  const openModal = useCallback(async (rowId) => {
    setSelectedRowId(rowId);
    setModalSubmitError('');
    
    // Загружаем сохраненные данные для этой строки или создаем пустые
    const savedData = savedModalData[rowId] || [{ id: 1, prichina: '', godpovr: '', priznaki: [], dolaprizn: '', specificDolaPrizn: '' }];
    // Преобразуем старый формат (prizn как строка) в новый (priznaki как массив)
    const convertedData = savedData.map(item => {
      if (item.prizn && !Array.isArray(item.priznaki)) {
        // Старый формат - преобразуем в новый
        return { ...item, priznaki: [item.prizn].filter(Boolean), prizn: undefined };
      }
      // Убеждаемся, что priznaki - массив
      return { ...item, priznaki: Array.isArray(item.priznaki) ? item.priznaki : [] };
    });
    setModalData(convertedData);
    setIsModalOpen(true);
    
    // Загружаем признаки для всех причин, которые есть в modalData
    const handbook = handbooks.find(h => h.id === 10);
    if (handbook && handbook.references && loadSignsForCauseRef.current) {
      const causesToLoad = new Set();
      convertedData.forEach(item => {
        if (item.prichina) {
          const cause = handbook.references.find(r => r.id.toString() === String(item.prichina));
          if (cause && cause.name && !loadedSigns[cause.name]) {
            causesToLoad.add(cause.name);
          }
        }
      });
      
      // Загружаем признаки для всех причин параллельно
      const loadPromises = Array.from(causesToLoad).map(causeName => loadSignsForCauseRef.current(causeName));
      await Promise.all(loadPromises);
    }
  }, [savedModalData, handbooks, loadedSigns]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRowId(null);
    setModalData([]);
    setModalSubmitError('');
  }, []);

  const addModalRow = () => {
    setModalSubmitError('');
    setModalData(prevModalData => {
      const newId = Math.max(...prevModalData.map(item => item.id), 0) + 1;
      return [...prevModalData, { id: newId, prichina: '', godpovr: '', priznaki: [], dolaprizn: '', specificDolaPrizn: '' }];
    });
  };

  const removeModalRow = (id) => {
    setModalSubmitError('');
    setModalData(prevModalData => {
      if (prevModalData.length > 1) {
        return prevModalData.filter(item => item.id !== id);
      }
      return prevModalData;
    });
  };

  const updateModalData = useCallback((id, field, value) => {
    setModalSubmitError('');
    setModalData(prevModalData => {
      return prevModalData.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    );
    });
  }, []);

  // Функция для добавления признака к элементу
  const addPriznakToItem = useCallback((itemId, priznakValue) => {
    setModalSubmitError('');
    setModalData(prevModalData => {
      return prevModalData.map(item => {
        if (item.id === itemId) {
          const priznaki = Array.isArray(item.priznaki) ? item.priznaki : [];
          if (!priznaki.includes(priznakValue)) {
            return { ...item, priznaki: [...priznaki, priznakValue] };
          }
          return item;
        }
        return item;
      });
    });
  }, []);

  // Функция для удаления признака из элемента
  const removePriznakFromItem = useCallback((itemId, priznakValue) => {
    setModalSubmitError('');
    setModalData(prevModalData => {
      return prevModalData.map(item => {
        if (item.id === itemId) {
          const priznaki = Array.isArray(item.priznaki) ? item.priznaki : [];
          return { ...item, priznaki: priznaki.filter(p => p !== priznakValue) };
        }
        return item;
      });
    });
  }, []);

  const handleModalDone = useCallback(() => {
    const selectedRow = (displayData?.data || []).find(item => item.id === selectedRowId);
    const causesHandbook = handbooks.find(h => h.id === 10);
    const causeLimitFieldByCode = {
      '821': 'stvtrdolap',
      '822': 'stburdolap',
      '881': 'svvtrdolap',
      '882': 'svburdolap'
    };
    const canFillSpecificDola = (prichinaId) => {
      if (!prichinaId || !causesHandbook?.references) {
        return false;
      }

      const selectedCause = causesHandbook.references.find(r => r.id.toString() === String(prichinaId));
      if (!selectedCause) {
        return false;
      }

      const causeType = selectedCause.type || selectedCause.disease_type || '';
      return causeType === 'DISEASES' || causeType === 'STEM_PESTS' || causeType === 'CONDITIONAL_ROTTENING' || causeType === 'OTHER';
    };
    const getCauseSpecificLimitField = (prichinaId) => {
      if (!prichinaId || !causesHandbook?.references) {
        return '';
      }

      const selectedCause = causesHandbook.references.find(r => r.id.toString() === String(prichinaId));
      if (!selectedCause) {
        return '';
      }

      const causeCode = selectedCause.code ? String(selectedCause.code) : '';
      return causeLimitFieldByCode[causeCode] || causeLimitFieldByCode[String(prichinaId)] || '';
    };

    const allowedDamageShare = [
      'osldolap',
      'sosldolap',
      'usdolap',
      'svsuhdolap',
      'svvtrdolap',
      'svburdolap',
      'stsuhdolap',
      'stvtrdolap',
      'stburdolap'
    ].reduce((sum, field) => sum + (parseFloat(selectedRow?.[field]) || 0), 0);

    const hasDamageShareMismatch = modalData.some(item => {
      const valueRaw = String(item?.dolaprizn ?? '').trim();
      if (!valueRaw) {
        return false;
      }

      const value = parseFloat(valueRaw);
      return !Number.isNaN(value) && value > allowedDamageShare;
    });

    if (hasDamageShareMismatch) {
      setModalSubmitError('Несоответствие доли поврежденных деревьев и распределения по категориям состояния');
      return;
    }

    const hasCauseCategoryMismatch = modalData.some(item => {
      const limitField = getCauseSpecificLimitField(item?.prichina);
      if (!limitField) {
        return false;
      }

      const totalDamageShareRaw = String(item?.dolaprizn ?? '').trim();
      if (!totalDamageShareRaw) {
        return false;
      }

      const totalDamageShare = parseFloat(totalDamageShareRaw);
      const allowedByCauseCategory = parseFloat(selectedRow?.[limitField]) || 0;
      return !Number.isNaN(totalDamageShare) && totalDamageShare > allowedByCauseCategory;
    });

    if (hasCauseCategoryMismatch) {
      setModalSubmitError('Несоответствие доли поврежденных деревьев и распределения по категориям состояния');
      return;
    }

    const hasSpecificDolaMismatch = modalData.some(item => {
      if (!canFillSpecificDola(item?.prichina)) {
        return false;
      }

      const totalDamageShareRaw = String(item?.dolaprizn ?? '').trim();
      const specificDamageShareRaw = String(item?.specificDolaPrizn ?? '').trim();
      if (!totalDamageShareRaw || !specificDamageShareRaw) {
        return false;
      }

      const totalDamageShare = parseFloat(totalDamageShareRaw);
      const specificDamageShare = parseFloat(specificDamageShareRaw);
      return !Number.isNaN(totalDamageShare) &&
        !Number.isNaN(specificDamageShare) &&
        specificDamageShare > totalDamageShare;
    });

    if (hasSpecificDolaMismatch) {
      setModalSubmitError('Несоответствие доли поврежденных деревьев в целом по породе и для расчета очага');
      return;
    }

    // Сохраняем данные modalData для выбранной строки в formData
    if (selectedRowId) {
      const currentFormData = formData || getEmptyFormData();
      const newFormData = {
        ...currentFormData,
        savedModalData: {
          ...(currentFormData?.savedModalData || {}),
          [selectedRowId]: modalData
        }
      };
      updateFormData(newFormData);
    }
    closeModal();
  }, [modalData, selectedRowId, closeModal, formData, updateFormData, displayData, handbooks]);

  // Функция-хелпер для сохранения данных очага (по ключу и по коду причины)
  const updateOchagData = useCallback((key, data) => {
    const newOchagData = { ...ochagData };
    
    // Получаем ID причины из ключа (формат: "ID-индекс")
    const causeId = key.split('-')[0];
    
    // Находим причину в справочнике для получения кода
    const causesHandbook = handbooks.find(h => h.id === 10);
    let causeCode = null;
    if (causesHandbook && causesHandbook.references) {
      const cause = causesHandbook.references.find(
        r => r.id.toString() === causeId
      );
      if (cause && cause.code) {
        causeCode = String(cause.code);
      }
    }
    
    // Получаем текущие данные по ключу (если есть)
    const currentDataByKey = newOchagData[key] || {};
    
    // Объединяем текущие данные с новыми
    const updatedData = {
      ...currentDataByKey,
      ...data
    };
    
    // Сохраняем данные по ключу с индексом (для редактирования)
    newOchagData[key] = updatedData;
    
    // Также сохраняем данные по коду причины (для отображения, как в Form6)
    if (causeCode) {
      // Получаем текущие данные по коду (если есть)
      const currentDataByCode = newOchagData[causeCode] || {};
      // Объединяем все данные (и старые по коду, и новые)
      newOchagData[causeCode] = {
        ...currentDataByCode,
        ...updatedData
      };
    }
    
    setOchagData(newOchagData);
  }, [ochagData, handbooks]);

  // Функция-хелпер для получения данных очага по ключу (ищет и по ключу, и по коду причины)
  const getOchagDataByKey = useCallback((key) => {
    // Сначала пробуем найти по ключу
    let data = ochagData[key] || null;
    
    // Если не нашли, пробуем найти по коду причины
    if (!data) {
      const causeId = key.split('-')[0];
      const causesHandbook = handbooks.find(h => h.id === 10);
      if (causesHandbook && causesHandbook.references) {
        const cause = causesHandbook.references.find(
          r => r.id.toString() === causeId
        );
        if (cause && cause.code) {
          const causeCode = String(cause.code);
          data = ochagData[causeCode] || null;
        }
      }
    }
    
    return data || {};
  }, [ochagData, handbooks]);

  // Функции для работы с picker справочника
  const openPicker = useCallback((fieldName, handbookId, itemId) => {
    const handbook = handbooks.find(h => h.id === handbookId);
    
    if (handbook && handbook.references) {
      const options = handbook.references.map(r => ({
        value: r.id.toString(),
        label: fieldName === 'prichina' && r.code ? `${r.name} (${r.code})` : r.name,
        description: r.description || null
      }));
      
      if (options && options.length > 0) {
        let currentValue = '';
        
        if (itemId) {
          // Для полей модального окна берем значение из конкретного элемента
          const item = modalData.find(m => m.id === itemId);
          currentValue = item ? item[fieldName] || '' : '';
          setActiveModalItemId(itemId);
        } else {
          setActiveModalItemId(null);
        }
        
        const selectedIndex = options.findIndex(opt => opt.value === currentValue);
        
        setPickerField(fieldName);
        setPickerOptions(options);
        setPickerSearch('');
        setSelectedOptionIndex(selectedIndex);
        setShowPicker(true);
      }
    }
  }, [handbooks, modalData]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setPickerField('');
    setPickerOptions([]);
    setPickerSearch('');
    setSelectedOptionIndex(-1);
    setActiveModalItemId(null);
  }, []);

  const selectPickerValue = async (value) => {
    const selectedOption = pickerOptions.find(opt => opt.value === value);
    const valueToSave = selectedOption ? selectedOption.value : value;
    
    if (pickerField === 'charochag') {
      // Обновляем основное поле характера усыхания (сохраняем ID)
      const currentFormData = formData || getEmptyFormData();
      const newFormData = { 
        ...currentFormData, 
        charochag: selectedOption ? selectedOption.value : valueToSave 
      };
      updateFormData(newFormData);
    } else if (pickerField === 'ochag_vo') {
      // Обработка выбора ВО для очага
      if (activeModalItemId) {
        updateOchagData(activeModalItemId, { vo: valueToSave });
      }
      closePicker();
    } else if (activeModalItemId) {
      // Обновляем данные в модальном окне
      updateModalData(activeModalItemId, pickerField, valueToSave);
      
      // Если выбрана причина, загружаем признаки
      if (pickerField === 'prichina' && selectedOption) {
        // Проверяем, изменилась ли причина
        const currentCause = previousCause[activeModalItemId];
        if (currentCause !== selectedOption.label) {
          // Очищаем массив признаков при смене причины
          updateModalData(activeModalItemId, 'priznaki', []);
          // Обновляем предыдущую причину
          setPreviousCause(prev => ({
            ...prev,
            [activeModalItemId]: selectedOption.label
          }));
        }
        if (loadSignsForCauseRef.current) {
          await loadSignsForCauseRef.current(selectedOption.label);
        }
      } else if (pickerField === 'prizn' && activeModalItemId) {
        // Для признаков множественный выбор обрабатывается в PickerModal через чекбоксы
        // Здесь не закрываем picker, чтобы можно было выбрать несколько
        return;
      }
    }
    
    closePicker();
  };

  const getFilteredOptions = useCallback(() => {
    let filtered = pickerOptions;
    
    if (pickerSearch.trim()) {
      const searchLower = pickerSearch.toLowerCase();
      filtered = pickerOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchLower) ||
        (opt.description && opt.description.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [pickerOptions, pickerSearch]);

  const getSelectedIndexInFiltered = useCallback(() => {
    const filtered = getFilteredOptions();
    if (selectedOptionIndex >= 0 && selectedOptionIndex < pickerOptions.length) {
      const selectedOption = pickerOptions[selectedOptionIndex];
      return filtered.findIndex(opt => opt.value === selectedOption.value);
    }
    return -1;
  }, [getFilteredOptions, selectedOptionIndex, pickerOptions]);

  // Функции для работы с picker года
  const openYearPicker = useCallback((itemId) => {
    setActiveYearItemId(itemId);
    setShowYearPicker(true);
  }, []);

  // Функция для открытия picker признаков
  const openSignsPicker = useCallback(async (itemId) => {
    const item = modalData.find(m => m.id === itemId);
    if (!item || !item.prichina) {
      alert('Сначала выберите причину');
      return;
    }
    
    // Получаем название причины из справочника
    const handbook = handbooks.find(h => h.id === 10);
    let causeName = '';
    if (handbook && handbook.references) {
      const selectedCause = handbook.references.find(r => r.id.toString() === item.prichina);
      causeName = selectedCause ? selectedCause.name : '';
    }
    
    if (!causeName) {
      alert('Не удалось определить причину');
      return;
    }
    
    // Получаем признаки для этой причины
    let signsOptions = loadedSigns[causeName] || [];
    
    // Если признаки не загружены, пытаемся загрузить их
    if (signsOptions.length === 0 && loadSignsForCauseRef.current) {
      const loadedOptions = await loadSignsForCauseRef.current(causeName);
      signsOptions = loadedOptions || [];
    }
    
    if (signsOptions.length === 0) {
      alert('Признаки для выбранной причины не найдены');
      return;
    }
    
    setActiveModalItemId(itemId);
    setPickerField('prizn');
    setPickerOptions(signsOptions);
    setPickerSearch('');
    setSelectedOptionIndex(-1);
    setShowPicker(true);
  }, [modalData, handbooks, loadedSigns]);

  const closeYearPicker = useCallback(() => {
    setShowYearPicker(false);
    setActiveYearItemId(null);
  }, []);

  const selectYear = useCallback((year) => {
    if (activeYearItemId) {
      updateModalData(activeYearItemId, 'godpovr', year.toString());
    }
    closeYearPicker();
  }, [activeYearItemId, updateModalData, closeYearPicker]);

  const generateYears = useCallback(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 1900; year--) {
      years.push(year);
    }
    return years;
  }, []);

  // Функции для работы со скроллбаром модального окна
  const handleModalSliderChange = useCallback((e) => {
    const percentage = parseFloat(e.target.value);
    setModalScrollPosition(percentage);
    
    if (modalScrollRef.current) {
      const maxScroll = modalScrollRef.current.scrollWidth - modalScrollRef.current.clientWidth;
      const scrollLeft = (percentage / 100) * maxScroll;
      modalScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  const handleModalScroll = useCallback(() => {
    if (modalScrollRef.current) {
      const maxScroll = modalScrollRef.current.scrollWidth - modalScrollRef.current.clientWidth;
      const currentScroll = modalScrollRef.current.scrollLeft;
      const percentage = (currentScroll / maxScroll) * 100;
      setModalScrollPosition(percentage);
    }
  }, []);

  // Функции для работы с характером усыхания и информацией пользователя
  const openDryingCharacterPicker = useCallback(() => {
    openPicker('charochag', 20);
  }, [openPicker]);

  const handleUserInfoChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Сохраняем в formData
    const currentFormData = formData || getEmptyFormData();
    const newFormData = { ...currentFormData, itaks4: newValue };
    updateFormData(newFormData);
  }, [formData, updateFormData]);

  // Синхронизация данных из Form2 - при первой инициализации или изменении количества строк или данных
  useEffect(() => {
    
    if (independentForm2Data && dataType && independentForm2Data.poroda_characteristics_dependent) {
      
      // Проверяем длину данных Form2
      const form2Length = independentForm2Data.poroda_characteristics_dependent.length;
      
      // Получаем текущие данные Form4 из состояния (не из зависимостей)
      const currentFormData = formData || getEmptyFormData();
      const currentForm4Rows = Array.isArray(currentFormData?.data) ? currentFormData.data : [];
      
      // Проверяем, изменились ли данные vozr, hsr, dsr в Form2
      const dataChanged = independentForm2Data.poroda_characteristics_dependent.some((item, index) => {
        const existingItem = currentFormData?.data?.[index];
        if (!existingItem) return true;
        
        const vozrFromForm2 = item.vozr || '';
        const hsrFromForm2 = item.hsr || '';
        const dsrFromForm2 = item.dsr || '';

        return existingItem.vozr !== vozrFromForm2 ||
               existingItem.hsr !== hsrFromForm2 || 
               existingItem.dsr !== dsrFromForm2;
      });
      
      // Синхронизируем только если:
      // 1. Это первая инициализация (содержательных данных еще нет И флаг не установлен)
      // 2. Количество строк в Form2 изменилось (используем ref для отслеживания)
      // НЕ синхронизируем если данные vozr, hsr, dsr изменились - эти поля обновляются отдельно
      const lengthChanged = form2Length !== prevForm2LengthRef.current;
      const hasMeaningfulData = currentForm4Rows.some(row =>
        String(row?.yarus ?? '').trim() !== '' ||
        String(row?.dolap ?? '').trim() !== '' ||
        String(row?.kodporoda ?? '').trim() !== ''
      );
      const shouldSync = (!hasMeaningfulData && !isInitializedRef.current) || lengthChanged;
      
      // Обновляем ref с текущей длиной
      prevForm2LengthRef.current = form2Length;
      
      if (shouldSync) {
        isInitializedRef.current = true;
        
        // Создаем новые данные на основе Form2
        const syncedData = independentForm2Data.poroda_characteristics_dependent.map((item, index) => {
          // Ищем существующие данные в Form4 по индексу (id = index + 1) для сохранения пользовательского ввода
          // Используем индекс, так как порядок элементов соответствует порядку в Form2
          const existingForm4Item = currentFormData?.data?.[index];
          
          // Если не нашли по индексу, пробуем найти по ярусу и породе (для обратной совместимости)
          const existingForm4ItemByMatch = existingForm4Item || (currentFormData?.data || []).find(existing => {
            const yarusMatch = String(existing.yarus) === String(item.yarus) || existing.yarus === item.yarus;
            const porodaMatch = existing.kodporoda === item.kodporodato || existing.poroda === item.poroda;
            return yarusMatch && porodaMatch;
          });
          
          // Извлекаем данные из variant4, если они есть
          const variant4 = item.variant4 || {};
          
          // Вычисляем bpodolap (100 минус сумма всех остальных категорий)
          const catII = parseFloat(variant4.catII || '0') || 0;
          const catIII = parseFloat(variant4.catIII || '0') || 0;
          const catIV = parseFloat(variant4.catIV || '0') || 0;
          const catVa = parseFloat(variant4.catVa || '0') || 0;
          const catVb = parseFloat(variant4.catVb || '0') || 0;
          const catVc = parseFloat(variant4.catVc || '0') || 0;
          const catVd = parseFloat(variant4.catVd || '0') || 0;
          const catVe = parseFloat(variant4.catVe || '0') || 0;
          const catVf = parseFloat(variant4.catVf || '0') || 0;
          
          const sumOther = catII + catIII + catIV + catVa + catVb + catVc + catVd + catVe + catVf;
          const bpodolap = existingForm4ItemByMatch?.bpodolap !== undefined 
            ? existingForm4ItemByMatch.bpodolap 
            : (sumOther > 0 ? Math.max(0, 100 - sumOther) : 100);
          
          // Для vozr, hsr, dsr всегда берем из Form2, так как эти поля только для чтения в Form4
          const vozrFromForm2 = item.vozr || '';
          const hsrFromForm2 = item.hsr || '';
          const dsrFromForm2 = item.dsr || '';
          
          return {
            id: existingForm4ItemByMatch?.id || (index + 1),
            yarus: item.yarus || '',
            // Берём долю/породу из новых полей dolap/kodporodato
            dolap: item.dolap || '',
            kodporoda: item.kodporodato || '',
            // Всегда берем vozr, hsr, dsr из Form2, так как эти поля только для чтения
            vozr: vozrFromForm2,
            hsr: hsrFromForm2,
            dsr: dsrFromForm2,
            // Используем данные из variant4, если они есть, иначе сохраняем существующие значения или значения по умолчанию
            // Приоритет: существующие данные > данные из variant4 > значения по умолчанию
            // ВАЖНО: сохраняем ВСЕ существующие значения, если они есть
            bpodolap: existingForm4ItemByMatch?.bpodolap !== undefined ? existingForm4ItemByMatch.bpodolap : bpodolap,
            osldolap: existingForm4ItemByMatch?.osldolap !== undefined ? existingForm4ItemByMatch.osldolap : (catII > 0 ? catII : 0),
            sosldolap: existingForm4ItemByMatch?.sosldolap !== undefined ? existingForm4ItemByMatch.sosldolap : (catIII > 0 ? catIII : 0),
            usdolap: existingForm4ItemByMatch?.usdolap !== undefined ? existingForm4ItemByMatch.usdolap : (catIV > 0 ? catIV : 0),
            svsuhdolap: existingForm4ItemByMatch?.svsuhdolap !== undefined ? existingForm4ItemByMatch.svsuhdolap : (catVa > 0 ? catVa : 0),
            svvtrdolap: existingForm4ItemByMatch?.svvtrdolap !== undefined ? existingForm4ItemByMatch.svvtrdolap : (catVb > 0 ? catVb : 0),
            svburdolap: existingForm4ItemByMatch?.svburdolap !== undefined ? existingForm4ItemByMatch.svburdolap : (catVc > 0 ? catVc : 0),
            stsuhdolap: existingForm4ItemByMatch?.stsuhdolap !== undefined ? existingForm4ItemByMatch.stsuhdolap : (catVd > 0 ? catVd : 0),
            stvtrdolap: existingForm4ItemByMatch?.stvtrdolap !== undefined ? existingForm4ItemByMatch.stvtrdolap : (catVe > 0 ? catVe : 0),
            stburdolap: existingForm4ItemByMatch?.stburdolap !== undefined ? existingForm4ItemByMatch.stburdolap : (catVf > 0 ? catVf : 0),
            sksporoda: existingForm4ItemByMatch?.sksporoda || '',
            ooporoda: existingForm4ItemByMatch?.ooporoda || '',
            toporoda: existingForm4ItemByMatch?.toporoda || ''
          };
        });
        
        const newFormData = {
          ...currentFormData,
          data: syncedData,
          charochag: currentFormData?.charochag || '',
          itaks4: currentFormData?.itaks4 || '',
          // Сохраняем savedModalData, если он есть
          savedModalData: currentFormData?.savedModalData || {}
        };
        updateFormData(newFormData);
      } else if (dataChanged && hasMeaningfulData) {
        // Если изменились только vozr, hsr, dsr (поля только для чтения), обновляем только их
        // НЕ перезаписываем пользовательские данные
        const updatedData = currentFormData.data.map((item, index) => {
          const form2Item = independentForm2Data.poroda_characteristics_dependent[index];
          if (!form2Item) return item;
          
          const vozrFromForm2 = form2Item.vozr || '';
          const hsrFromForm2 = form2Item.hsr || '';
          const dsrFromForm2 = form2Item.dsr || '';
          
          // Обновляем только поля только для чтения, сохраняя все остальные данные
          return {
            ...item,
            vozr: vozrFromForm2,
            hsr: hsrFromForm2,
            dsr: dsrFromForm2
          };
        });
        
        const newFormData = {
          ...currentFormData,
          data: updatedData
        };
        updateFormData(newFormData);
      }
    }
  }, [independentForm2Data, dataType, updateFormData, isActive, formData]);

  // НЕ сбрасываем флаг инициализации при деактивации формы
  // Это предотвращает повторную синхронизацию при возврате на форму

  // Валидация обязательных полей Form4 - все поля необязательные
  useEffect(() => {
    // Все поля необязательные, поэтому ошибок валидации нет
    const hasErrors = false;
    
    if (hasValidationErrors !== hasErrors) {
      setHasValidationErrors(hasErrors);
    }
  }, [hasValidationErrors]);

  // Обработчики для слайдеров
  const handleTableScroll = useCallback(() => {
    if (tableScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setTableScrollPosition(percentage);
    }
  }, []);

  const handleTableSliderChange = useCallback((e) => {
    const newPosition = parseInt(e.target.value);
    setTableScrollPosition(newPosition);
    if (tableScrollRef.current) {
      const maxScroll = tableScrollRef.current.scrollWidth - tableScrollRef.current.clientWidth;
      const scrollLeft = (newPosition / 100) * maxScroll;
      tableScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  const handleCalculationsScroll = useCallback(() => {
    if (calculationsScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = calculationsScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setCalculationsScrollPosition(percentage);
    }
  }, []);

  const handleCalculationsSliderChange = useCallback((e) => {
    const newPosition = parseInt(e.target.value);
    setCalculationsScrollPosition(newPosition);
    if (calculationsScrollRef.current) {
      const maxScroll = calculationsScrollRef.current.scrollWidth - calculationsScrollRef.current.clientWidth;
      const scrollLeft = (newPosition / 100) * maxScroll;
      calculationsScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Инициализация слайдеров
  useEffect(() => {
    const handleTableScroll = () => {
      if (tableScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
        const maxScroll = scrollWidth - clientWidth;
        const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
        setTableScrollPosition(percentage);
      }
    };

    const handleCalculationsScroll = () => {
      if (calculationsScrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = calculationsScrollRef.current;
        const maxScroll = scrollWidth - clientWidth;
        const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
        setCalculationsScrollPosition(percentage);
      }
    };

    // Устанавливаем обработчики событий
    const tableElement = tableScrollRef.current;
    const calculationsElement = calculationsScrollRef.current;

    if (tableElement) {
      tableElement.addEventListener('scroll', handleTableScroll);
    }

    if (calculationsElement) {
      calculationsElement.addEventListener('scroll', handleCalculationsScroll);
    }

    // Инициализируем позиции слайдеров
    handleTableScroll();
    handleCalculationsScroll();

    // Очистка обработчиков при размонтировании
    return () => {
      if (tableElement) {
        tableElement.removeEventListener('scroll', handleTableScroll);
      }
      if (calculationsElement) {
        calculationsElement.removeEventListener('scroll', handleCalculationsScroll);
      }
    };
  }, []);

  return (
    <>
      <div className="template-section">
        <div 
          className="template-header" 
          style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h3>IV. Форма ввода ВНН за санитарным состоянием насаждений глазомерным методом</h3>
        </div>
        
        <div className="template-content">
          {/* Таблица Данные */}
          <DataSection
            displayData={displayData}
            updateDataItem={updateDataItem}
            openModal={openModal}
            savedModalData={savedModalData}
            tableScrollRef={tableScrollRef}
            tableScrollPosition={tableScrollPosition}
            handleTableSliderChange={handleTableSliderChange}
            handleTableScroll={handleTableScroll}
            handbooks={handbooks}
          />

        {/* Таблица расчетов */}
        <CalculationsSection
          displayData={displayData}
          calculateTotalsByNasazhdeniya={calculateTotalsByNasazhdeniya}
          calculationsScrollRef={calculationsScrollRef}
          calculationsScrollPosition={calculationsScrollPosition}
          handleCalculationsSliderChange={handleCalculationsSliderChange}
          handleCalculationsScroll={handleCalculationsScroll}
          handbooks={handbooks}
          savedModalData={savedModalData}
          independentForm2Data={independentForm2Data}
          form1Data={form1Data}
          dataType={dataType}
        />

        {/* Карточки с очагами */}
        {causesWithOchag.length > 0 && (
          <div style={{ 
            marginTop: '30px', 
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ 
              margin: '0 0 20px 0', 
              fontSize: '16px', 
              fontWeight: '600',
              color: '#212529'
            }}>
              Очаги
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '15px'
            }}>
              {causesWithOchag.map((cause, index) => {
                const causeKey = `${cause.id}-${index}`;
                // Проверяем, выбран ли очаг по текущему causeKey или по любому ключу с таким же ID
                const isSelected = selectedOchags.has(causeKey) || 
                  Array.from(selectedOchags).some(key => key.startsWith(`${cause.id}-`));
                const isDisabled = !isSelected && selectedOchags.size >= 3;

                return (
                  <div
                    key={causeKey}
                    style={{
                      backgroundColor: 'white',
                      padding: '15px',
                      borderRadius: '6px',
                      border: '1px solid #dee2e6',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      position: 'relative',
                      opacity: isDisabled ? 0.6 : 1,
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      
                      // Открываем модальное окно если клик не на чекбокс
                      if (e.target.type !== 'checkbox' && e.target.tagName !== 'INPUT') {
                        
                        // Если очаг не выбран, автоматически выбираем его (если не превышен лимит)
                        if (!isSelected) {
                          const newSelected = new Set(selectedOchags);
                          // Удаляем старые ключи с таким же ID причины
                          Array.from(newSelected).forEach(key => {
                            if (key.startsWith(`${cause.id}-`)) {
                              newSelected.delete(key);
                            }
                          });
                          if (newSelected.size < 3) {
                            newSelected.add(causeKey);
                            setSelectedOchags(newSelected);
                          } else {
                            return; // Не открываем модальное окно, если лимит превышен
                          }
                        }
                        
                        setEditingOchagKey(causeKey);
                        setIsOchagModalOpen(true);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newSelected = new Set(selectedOchags);
                        if (e.target.checked) {
                          // Удаляем старые ключи с таким же ID причины
                          Array.from(newSelected).forEach(key => {
                            if (key.startsWith(`${cause.id}-`)) {
                              newSelected.delete(key);
                            }
                          });
                          if (newSelected.size < 3) {
                            newSelected.add(causeKey);
                          }
                        } else {
                          // Удаляем все ключи с таким же ID причины
                          Array.from(newSelected).forEach(key => {
                            if (key.startsWith(`${cause.id}-`)) {
                              newSelected.delete(key);
                            }
                          });
                        }
                        setSelectedOchags(newSelected);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        width: '18px',
                        height: '18px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        zIndex: 10,
                        accentColor: '#6c757d'
                      }}
                    />
                    <div style={{ 
                      marginBottom: '10px',
                      paddingLeft: '30px'
                    }}>
                      <strong style={{ 
                        fontSize: '14px', 
                        color: '#212529',
                        display: 'block',
                        marginBottom: '10px'
                      }}>
                        {cause.code ? `${cause.name} (${cause.code})` : cause.name}
                      </strong>
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#495057',
                        fontWeight: '500',
                        marginBottom: '5px'
                      }}>
                        Степень: <span style={{ 
                          fontWeight: '600'
                        }}>
                          {cause.state}
                        </span>
                      </div>
                      {/* Показываем "Доля повр. деревьев" для формы №4 */}
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#495057',
                        fontWeight: '500',
                        marginBottom: '5px'
                      }}>
                        Доля повр. деревьев: <span style={{ 
                          fontWeight: '600'
                        }}>
                          {(() => {
                            const value = getOchagDamageValueForCause(cause.id);
                            return value ? `${value}%` : '-';
                          })()}
                        </span>
                      </div>
                      {(() => {
                        // Находим данные очага по коду причины (точно как в Form6)
                        const causeCode = cause.code ? String(cause.code) : null;
                        let ochagInfo = causeCode ? (ochagData[causeCode] || {}) : {};
                        
                        // Если не нашли по коду, пробуем найти по ключу с ID (для обратной совместимости)
                        if (Object.keys(ochagInfo).length === 0) {
                          const ochagDataKey = Array.from(Object.keys(ochagData)).find(key => key.startsWith(`${cause.id}-`));
                          if (ochagDataKey) {
                            ochagInfo = ochagData[ochagDataKey] || {};
                          }
                        }
                        
                        // Отображаем данные, если они есть (точно как в Form6)
                        return Object.keys(ochagInfo).length > 0 && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6c757d',
                            marginTop: '8px',
                            paddingTop: '8px',
                            borderTop: '1px solid #e9ecef'
                          }}>
                            {ochagInfo.isMain && (
                              <div style={{ marginBottom: '4px' }}>
                                <strong>Главный очаг</strong>
                              </div>
                            )}
                            {ochagInfo.vo && (
                              <div style={{ marginBottom: '4px' }}>
                                ВО: {(() => {
                                  const voHandbook = handbooks.find(h => h.id === 40);
                                  if (voHandbook && voHandbook.references) {
                                    const vo = voHandbook.references.find(
                                      r => r.id.toString() === String(ochagInfo.vo)
                                    );
                                    return vo ? vo.name : ochagInfo.vo;
                                  }
                                  return ochagInfo.vo;
                                })()}
                              </div>
                            )}
                            {ochagInfo.isMain && ochagInfo.phase && (
                              <div style={{ marginBottom: '4px' }}>
                                Фаза: {(() => {
                                  const phaseMap = {
                                    'INITIAL': 'Начальная',
                                    'POPULATION_GROWTH': 'Нарастания численности',
                                    'OUTBREAK': 'Собственно вспышка',
                                    'CRISIS': 'Кризис',
                                    'EMERGING': 'Возникающий',
                                    'ACTIVE': 'Действующий',
                                    'FADING': 'Затухающий'
                                  };
                                  // Поддержка старых значений для обратной совместимости
                                  if (ochagInfo.phase === 'arising') return 'Возникающая';
                                  if (ochagInfo.phase === 'acting') return 'Действующая';
                                  return phaseMap[ochagInfo.phase] || ochagInfo.phase;
                                })()}
                              </div>
                            )}
                            {ochagInfo.isMain && ochagInfo.type && (
                              <div style={{ marginBottom: '4px' }}>
                                Тип: {(() => {
                                  if (ochagInfo.type === 'episodic' || ochagInfo.type === 'EPISODIC' || ochagInfo.type === 'ACUTE') {
                                    return 'Эпизодический';
                                  } else if (ochagInfo.type === 'chronic' || ochagInfo.type === 'CHRONIC') {
                                    return 'Хронический';
                                  }
                                  return ochagInfo.type;
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Таблица Информация */}
        <DryingCharacterSection
          displayData={displayData}
          handbooks={handbooks}
          openDryingCharacterPicker={openDryingCharacterPicker}
          handleUserInfoChange={handleUserInfoChange}
        />
      </div>
      </div>

      {/* Модальное окно для редактирования очага */}
      {(() => {
        return isOchagModalOpen && editingOchagKey;
      })() && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            setIsOchagModalOpen(false);
            setEditingOchagKey(null);
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '10px'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                Редактирование очага
              </h3>
              <button
                onClick={() => {
                  setIsOchagModalOpen(false);
                  setEditingOchagKey(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div 
                style={{ 
                  overflowX: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <table className="table" style={{ marginBottom: 0, minWidth: '800px' }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}>Главный очаг</th>
                      <th style={{ minWidth: '200px', backgroundColor: '#f8f9fa' }}>{getOchagDataByKey(editingOchagKey)?.isMain ? 'ВО' : 'Вид ВО'}</th>
                      {getOchagDataByKey(editingOchagKey)?.isMain && (
                        <>
                          <th style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}>Фаза</th>
                          <th style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}>Тип</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ 
                        padding: '0',
                        border: '1px solid #dee2e6',
                        textAlign: 'center'
                      }}>
                        <label style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          margin: 0,
                          minHeight: '40px',
                          padding: '8px'
                        }}>
                          <input
                            type="checkbox"
                            checked={getOchagDataByKey(editingOchagKey)?.isMain || false}
                            onChange={(e) => {
                              const isMain = e.target.checked;
                              const currentData = getOchagDataByKey(editingOchagKey);
                              
                              // Если делаем главным, убираем главный статус у других очагов
                              if (isMain) {
                                // Сначала убираем главный статус у всех других очагов
                                const newOchagData = { ...ochagData };
                                Object.keys(newOchagData).forEach(key => {
                                  if (key !== editingOchagKey) {
                                    // Проверяем, относится ли этот ключ к другому очагу
                                    const keyCauseId = key.includes('-') ? key.split('-')[0] : null;
                                    const editingCauseId = editingOchagKey.split('-')[0];
                                    
                                    // Если это другой очаг (другой ID причины) и он главный
                                    if (keyCauseId && keyCauseId !== editingCauseId && newOchagData[key]?.isMain) {
                                      newOchagData[key] = { ...newOchagData[key], isMain: false, phase: '', type: '' };
                                    }
                                    // Также проверяем по коду причины
                                    if (!key.includes('-')) {
                                      // Это код причины, проверяем, не совпадает ли с текущим
                                      const causesHandbook = handbooks.find(h => h.id === 10);
                                      if (causesHandbook && causesHandbook.references) {
                                        const cause = causesHandbook.references.find(
                                          r => r.code && String(r.code) === key
                                        );
                                        if (cause && String(cause.id) !== editingCauseId && newOchagData[key]?.isMain) {
                                          newOchagData[key] = { ...newOchagData[key], isMain: false, phase: '', type: '' };
                                        }
                                      }
                                    }
                                  }
                                });
                                
                                // Устанавливаем главный статус для текущего очага
                                newOchagData[editingOchagKey] = { ...currentData, isMain: true };
                                
                                // Также обновляем по коду причины
                                const causeId = editingOchagKey.split('-')[0];
                                const causesHandbook = handbooks.find(h => h.id === 10);
                                if (causesHandbook && causesHandbook.references) {
                                  const cause = causesHandbook.references.find(
                                    r => r.id.toString() === causeId
                                  );
                                  if (cause && cause.code) {
                                    const causeCode = String(cause.code);
                                    newOchagData[causeCode] = { ...currentData, isMain: true };
                                  }
                                }
                                
                                setOchagData(newOchagData);
                              } else {
                                // Если убираем главный статус, очищаем фазу и тип
                                updateOchagData(editingOchagKey, {
                                  isMain: false,
                                  phase: '',
                                  type: ''
                                });
                              }
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        </label>
                      </td>
                      <td style={{ 
                        padding: '0',
                        border: '1px solid #dee2e6',
                        textAlign: 'center'
                      }}>
                        <div
                          className="reference-cell"
                          onClick={() => {
                            const voHandbook = handbooks.find(h => h.id === 40);
                            const causesHandbook = handbooks.find(h => h.id === 10);
                            const causeId = editingOchagKey.split('-')[0];
                            const selectedCause = causesHandbook?.references?.find(
                              r => r.id.toString() === String(causeId)
                            );
                            const allowedDiseaseCodes = new Set(
                              Array.isArray(selectedCause?.disease_codes)
                                ? selectedCause.disease_codes.map(code => String(code))
                                : []
                            );

                            if (voHandbook && voHandbook.references) {
                              const options = voHandbook.references
                                .filter(r => {
                                  if (allowedDiseaseCodes.size === 0) {
                                    return false;
                                  }
                                  return allowedDiseaseCodes.has(String(r.code));
                                })
                                .map(r => ({
                                  value: r.id.toString(),
                                  label: r.name
                                }));
                              setPickerField('ochag_vo');
                              setPickerOptions(options);
                              setPickerSearch('');
                              const currentVo = getOchagDataByKey(editingOchagKey)?.vo || '';
                              const selectedIndex = options.findIndex(opt => opt.value === currentVo);
                              setSelectedOptionIndex(selectedIndex);
                              setShowPicker(true);
                              setActiveModalItemId(editingOchagKey);
                            }
                          }}
                          style={{ 
                            width: '100%',
                            height: '100%',
                            minHeight: '40px',
                            cursor: 'pointer',
                            background: 'transparent',
                            border: 'none',
                            padding: '0',
                            margin: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <span style={{ 
                            color: getOchagDataByKey(editingOchagKey)?.vo ? '#000' : '#6c757d',
                            fontSize: '14px',
                            flex: 1,
                            padding: '8px'
                          }}>
                            {(() => {
                              const currentOchagData = getOchagDataByKey(editingOchagKey);
                              if (!currentOchagData?.vo) return 'Выберите ВО';
                              const voHandbook = handbooks.find(h => h.id === 40);
                              if (voHandbook && voHandbook.references) {
                                const vo = voHandbook.references.find(
                                  r => r.id.toString() === String(currentOchagData.vo)
                                );
                                return vo ? vo.name : currentOchagData.vo;
                              }
                              return currentOchagData.vo;
                            })()}
                          </span>
                          <span style={{ color: '#6c757d', fontSize: '12px', paddingRight: '8px' }}>☰</span>
                        </div>
                      </td>
                      {ochagData[editingOchagKey]?.isMain && (
                        <>
                          <td style={{ 
                            padding: '0',
                            border: '1px solid #dee2e6',
                            textAlign: 'center'
                          }}>
                            <select
                              value={getOchagDataByKey(editingOchagKey)?.phase || ''}
                              onChange={(e) => {
                                updateOchagData(editingOchagKey, { phase: e.target.value });
                              }}
                              style={{
                                width: '100%',
                                height: '100%',
                                minHeight: '40px',
                                padding: '8px 12px',
                                border: 'none',
                                borderRadius: '0',
                                fontSize: '14px',
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                                color: '#212529',
                                outline: 'none'
                              }}
                            >
                              <option value="">Выберите фазу</option>
                              <option value="INITIAL">Начальная</option>
                              <option value="POPULATION_GROWTH">Нарастания численности</option>
                              <option value="OUTBREAK">Собственно вспышка</option>
                              <option value="CRISIS">Кризис</option>
                              <option value="EMERGING">Возникающий</option>
                              <option value="ACTIVE">Действующий</option>
                              <option value="FADING">Затухающий</option>
                            </select>
                          </td>
                          <td style={{ 
                            padding: '0',
                            border: '1px solid #dee2e6',
                            textAlign: 'center'
                          }}>
                            <select
                              value={getOchagDataByKey(editingOchagKey)?.type || ''}
                              onChange={(e) => {
                                updateOchagData(editingOchagKey, { type: e.target.value });
                              }}
                              style={{
                                width: '100%',
                                height: '100%',
                                minHeight: '40px',
                                padding: '8px 12px',
                                border: 'none',
                                borderRadius: '0',
                                fontSize: '14px',
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                                color: '#212529',
                                outline: 'none'
                              }}
                            >
                              <option value="">Выберите тип</option>
                              <option value="episodic">Эпизодический</option>
                              <option value="chronic">Хронический</option>
                            </select>
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {(() => {
              const closeModal = () => {
                setIsOchagModalOpen(false);
                setEditingOchagKey(null);
              };
              const cur = getOchagDataByKey(editingOchagKey) || {};
              const isMain = !!cur.isMain;
              const voOk = cur.vo != null && String(cur.vo).trim() !== '';
              const phaseOk = !isMain || (cur.phase != null && String(cur.phase).trim() !== '');
              const typeOk = !isMain || (cur.type != null && String(cur.type).trim() !== '');
              const isComplete = voOk && phaseOk && typeOk;
              return (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  marginTop: '20px',
                  paddingTop: '15px',
                  borderTop: '1px solid #eee'
                }}>
                  <button
                    onClick={closeModal}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#fff',
                      color: '#495057',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Закрыть
                  </button>
                  <button
                    onClick={closeModal}
                    disabled={!isComplete}
                    title={isComplete ? '' : 'Заполните все поля очага'}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: isComplete ? '#495058' : '#fff',
                      color: isComplete ? '#fff' : '#adb5bd',
                      border: `1px solid ${isComplete ? '#495058' : '#ced4da'}`,
                      borderRadius: '4px',
                      cursor: isComplete ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Готово
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Модальное окно для причины/признака */}
      <CauseSignModal
        isModalOpen={isModalOpen}
        selectedRowId={selectedRowId}
        modalData={modalData}
        handbooks={handbooks}
        loadedSigns={loadedSigns}
        isLoadingSigns={isLoadingSigns}
        openPicker={openPicker}
        openYearPicker={openYearPicker}
        openSignsPicker={openSignsPicker}
        addModalRow={addModalRow}
        removeModalRow={removeModalRow}
        closeModal={closeModal}
        handleModalDone={handleModalDone}
        modalScrollRef={modalScrollRef}
        modalScrollPosition={modalScrollPosition}
        handleModalSliderChange={handleModalSliderChange}
        handleModalScroll={handleModalScroll}
        updateModalData={updateModalData}
        addPriznakToItem={addPriznakToItem}
        removePriznakFromItem={removePriznakFromItem}
        sosnyakValue={sosnyakValue}
        submitError={modalSubmitError}
      />

      {/* Picker для справочников */}
      <PickerModal
        showPicker={showPicker}
        pickerSearch={pickerSearch}
        setPickerSearch={setPickerSearch}
        getFilteredOptions={getFilteredOptions}
        getSelectedIndexInFiltered={getSelectedIndexInFiltered}
        pickerField={pickerField}
        pickerValue={(() => {
          if (pickerField === 'charochag') {
            return displayData.charochag || '';
          } else if (pickerField === 'ochag_vo' && activeModalItemId) {
            return ochagData[activeModalItemId]?.vo || '';
          } else if (pickerField === 'prizn' && activeModalItemId) {
            const item = modalData.find(m => m.id === activeModalItemId);
            return item && Array.isArray(item.priznaki) ? item.priznaki : [];
          } else if (activeModalItemId) {
            const item = modalData.find(m => m.id === activeModalItemId);
            return item ? item[pickerField] || '' : '';
          }
          return '';
        })()}
        selectPickerValue={selectPickerValue}
        closePicker={closePicker}
        addPriznakToItem={pickerField === 'prizn' ? addPriznakToItem : null}
        removePriznakFromItem={pickerField === 'prizn' ? removePriznakFromItem : null}
        activeModalItemId={pickerField === 'prizn' ? activeModalItemId : null}
      />

      {/* Picker для выбора года */}
      <YearPickerModal
        showYearPicker={showYearPicker}
        selectYear={selectYear}
        closeYearPicker={closeYearPicker}
        activeYearItemId={activeYearItemId}
        modalData={modalData}
      />

    </>
  );
};

export default Form4;
